/**
 * Demo create mutation (S3) — a clone of `useCreateBooking`'s shape: the
 * service returns the envelope, the HOOK throws on failure so React Query's
 * onError fires, and the same caches are invalidated (a demo occupies a bay
 * like any booking). The cancel twin lives in `useCancelDemoBooking.ts`,
 * matching the standard hooks' one-file-per-mutation layout.
 *
 * @module booking/hooks/useCreateDemoBooking
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { demoBookingService, type CreateDemoBookingPayload } from '../api/demoBookingService';
import type { ApiCreateBookingResponse } from '../api/types';

export interface UseCreateDemoBookingOptions {
  onSuccess?: (result: ApiCreateBookingResponse) => void;
  onError?: (error: Error) => void;
}

export function useCreateDemoBooking(options?: UseCreateDemoBookingOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateDemoBookingPayload) => {
      const result = await demoBookingService.createDemoBooking(payload);

      if (!result.success) {
        // The API's member-facing message — surfaced by onError as a toast.
        throw new Error(result.message || 'Failed to book your demo');
      }

      return result.payload;
    },

    onSuccess: (result, payload) => {
      const { booking } = result;
      queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.all(booking.clubId)
      });
      // bookings.detail() is not club-prefixed — invalidate explicitly (same
      // reasoning as useCreateBooking).
      queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.detail(booking.id),
      });
      const bookingDate = payload.startTime.split('T')[0];
      queryClient.invalidateQueries({
        queryKey: queryKeys.bays.availabilityForDate(booking.clubId, payload.bayId, bookingDate),
      });

      options?.onSuccess?.(result);
    },

    onError: (error: Error) => {
      console.error('[useCreateDemoBooking] Error:', error);
      options?.onError?.(error);
    },
  });
}
